import type {
  NotificationCategoryDto,
  NotificationCategoryId,
  NotificationPreferencesDto,
} from '../../api/notificationTypes';

export type CategoryState = 'all' | 'some' | 'none';

export function categoryState(category: NotificationCategoryDto): CategoryState {
  const on = category.types.filter((t) => t.enabled).length;
  if (on === 0) return 'none';
  return on === category.types.length ? 'all' : 'some';
}

export function countNotifications(categories: NotificationCategoryDto[]): {
  enabled: number;
  total: number;
} {
  const types = categories.flatMap((c) => c.types);
  return { enabled: types.filter((t) => t.enabled).length, total: types.length };
}

function withTypes(
  category: NotificationCategoryDto,
  types: NotificationCategoryDto['types'],
): NotificationCategoryDto {
  return { ...category, types, enabled: types.some((t) => t.enabled) };
}

/** Optimistic local result of switching one type. */
export function applyType(
  prefs: NotificationPreferencesDto,
  type: string,
  enabled: boolean,
): NotificationPreferencesDto {
  return {
    ...prefs,
    categories: prefs.categories.map((c) =>
      c.types.some((t) => t.type === type)
        ? withTypes(
            c,
            c.types.map((t) => (t.type === type ? { ...t, enabled } : t)),
          )
        : c,
    ),
  };
}

/** Optimistic local result of switching a whole category. */
export function applyCategory(
  prefs: NotificationPreferencesDto,
  category: NotificationCategoryId,
  enabled: boolean,
): NotificationPreferencesDto {
  return {
    ...prefs,
    categories: prefs.categories.map((c) =>
      c.category === category ? withTypes(c, c.types.map((t) => ({ ...t, enabled }))) : c,
    ),
  };
}

/** Optimistic local result of switching everything. */
export function applyAll(
  prefs: NotificationPreferencesDto,
  enabled: boolean,
): NotificationPreferencesDto {
  return {
    ...prefs,
    categories: prefs.categories.map((c) => withTypes(c, c.types.map((t) => ({ ...t, enabled })))),
  };
}
